import axiosInstance from './axiosConfig'
const store = require('./store').default

const getMessage = (itemMessage) => {
  if (itemMessage === null || itemMessage === undefined) return ''
  const text = String(itemMessage).trim()
  return text ? ` ${text}` : ''
}

const getResponseMessage = (data) => {
  if (!data) return ''
  if (typeof data === 'string') return data
  const knownKeys = ['fileTooLarge', 'unsupportedFileType', 'tooManyFiles', 'imageOptimizationError', 'message', 'error', 'details']
  for (const key of knownKeys) {
    if (typeof data[key] === 'string' && data[key].trim()) return data[key]
  }
  if (typeof data.message === 'string' && data.message.trim()) return data.message
  if (Array.isArray(data) && typeof data[0]?.message === 'string' && data[0].message.trim()) return data[0].message
  return ''
}

const WEB_IMAGE_MAX_SIZE = 10 * 1024 * 1024
const WEB_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

const appendFormDataValue = (formData, key, value) => {
  if (Array.isArray(value)) {
    const hasOnlyFiles = value.length > 0 && value.every((item) => item instanceof File || item instanceof Blob)

    if (hasOnlyFiles) {
      value.forEach((file) => {
        formData.append(key === 'files' ? 'file' : key, file)
      })
    } else {
      formData.append(key, JSON.stringify(value))
    }
  } else if (value !== null && value !== undefined && typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
    formData.append(key, JSON.stringify(value))
  } else if (value !== null && value !== undefined) {
    formData.append(key, value)
  }
}

const buildFormData = (object = {}) => {
  const formData = new FormData()
  Object.keys(object).forEach((key) => appendFormDataValue(formData, key, object[key]))
  return formData
}

const getImageValidationMessage = (file) => {
  if (!WEB_IMAGE_TYPES.has(file?.type)) {
    return `O arquivo "${file?.name || 'selecionado'}" não é uma imagem JPEG, PNG ou WebP.`
  }

  if (Number(file?.size || 0) > WEB_IMAGE_MAX_SIZE) {
    return `A imagem "${file.name}" excede o limite de 10 MB.`
  }

  return ''
}

const validateImageFiles = (files = [], { notify = false } = {}) => {
  const imageFiles = Array.from(files || [])
  const message = imageFiles.map(getImageValidationMessage).find(Boolean) || ''

  if (message && notify) {
    store.dispatch('setError', { message })
  }

  return {
    valid: !message,
    message,
  }
}

const buildFileUploadError = (err, fallbackMessage = 'Erro ao enviar arquivo!') => {
  const message = getResponseMessage(err.response?.data) || (err.response ? fallbackMessage : 'Falha de conexão durante o envio do arquivo.')
  return {
    error: true,
    ...(err.response || {}),
    status: err.response?.status,
    message,
  }
}

const get = async (route, params = {}) => {
  const result = await axiosInstance
    .get(route, params)
    .then((res) => {
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      return { error: true, ...err.response, status: err.response?.status }
    })

  return result
}

const search = async (route, objectParams) => {
  const result = await axiosInstance
    .get(route, { params: objectParams })
    .then((res) => {
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      return { error: true, ...err.response, status: err.response?.status }
    })
  return result
}

const insert = async (route, object, itemMessage) => {
  const message = getMessage(itemMessage)
  let headers = {}
  for (let key in object) {
    if (object[key] instanceof File) {
      headers = { 'Content-Type': 'multipart/form-data' }
    }
  }
  const result = await axiosInstance
    .post(route, object, {
      headers: headers,
    })
    .then((res) => {
      const backendMessage = getResponseMessage(res.data)
      store.dispatch('setSuccess', {
        message: backendMessage || `Sucesso ao cadastrar${message}!`,
      })
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      const backendMessage = getResponseMessage(err.response?.data)
      store.dispatch('setError', {
        message: backendMessage || `Erro ao cadastrar${message}!`,
      })

      return { error: true, ...err.response, status: err.response.status }
    })
  return result
}

const update = async (route, id, object, itemMessage) => {
  const message = getMessage(itemMessage)
  const result = await axiosInstance
    .patch(`/${route}/${id}`, object)
    .then((res) => {
      const backendMessage = getResponseMessage(res.data)
      store.dispatch('setSuccess', {
        message: backendMessage || `Sucesso ao atualizar${message}!`,
      })
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      const backendMessage = getResponseMessage(err.response?.data)
      store.dispatch('setError', {
        message: backendMessage || `Erro ao atualizar${message}!`,
      })
      return { error: true, ...err.response, status: err.response?.status }
    })
  return result
}

const remove = async (route, id, itemMessage) => {
  const result = await axiosInstance
    .delete(`/${route}/${id}`)
    .then((res) => {
      store.dispatch('setSuccess', {
        message: `Sucesso ao remover ${itemMessage}!`,
      })

      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      console.log(err)

      store.dispatch('setError', {
        message: err.response?.data?.[0]?.message || `Erro ao remover ${itemMessage}!`,
      })

      return { error: true, ...err.response, status: err.response.status }
    })
  return result
}

const batchRemove = async (route, itemsArray, itemMessage) => {
  try {
    const result = await axiosInstance
      .delete(`/${route}/batch-remove`, { data: { items: itemsArray } })
      .then((res) => {
        store.dispatch('setSuccess', {
          message: `Sucesso ao remover ${itemMessage}!`,
        })

        return { ...res.data, status: res.status }
      })
      .catch((err) => {
        console.log(err)

        store.dispatch('setError', {
          message: `Erro ao remover ${itemMessage}!`,
        })

        return { error: true, ...err.response.data, status: err.response.status }
      })

    return result
  } catch (error) {
    console.log(error)
    store.dispatch('setError', {
      message: `Erro ao remover ${itemMessage}!`,
    })

    return { error: true, message: error.message, status: 500 }
  }
}

const validateCurrentPassword = async (route, object) => {
  const result = await axiosInstance
    .post(route, object)
    .then((res) => {
      return res
    })
    .catch((err) => {
      return err
    })
  return result
}

const insertFile = async (
  route,
  object,
  itemName = null,
  successMessage = null,
) => {
  const formData = buildFormData(object)

  const result = await axiosInstance
    .post(route, formData)
    .then((res) => {
      store.dispatch('setSuccess', {
        message: successMessage || 'Sucesso ao enviar arquivo!',
      })
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      const result = buildFileUploadError(err)
      store.dispatch('setError', {
        message: result.message,
      })
      return result
    })
  return result
}

const insertImageFiles = async (route, files = [], { data = {}, onProgress = null } = {}) => {
  const imageFiles = Array.from(files || [])
  if (!imageFiles.length) {
    return { error: true, completed: 0, total: 0, message: 'Nenhuma imagem selecionada.' }
  }

  const { message: validationMessage } = validateImageFiles(imageFiles)
  if (validationMessage) {
    store.dispatch('setError', { message: validationMessage })
    return { error: true, completed: 0, total: imageFiles.length, message: validationMessage }
  }

  for (let index = 0; index < imageFiles.length; index += 1) {
    const file = imageFiles[index]
    const formData = buildFormData({ ...data, files: [file] })

    try {
      await axiosInstance.post(route, formData, {
        onUploadProgress: (event) => {
          const percent = event.total ? Math.round((event.loaded / event.total) * 100) : 0
          onProgress?.({
            current: index + 1,
            total: imageFiles.length,
            percent,
            fileName: file.name,
          })
        },
      })
    } catch (err) {
      const result = buildFileUploadError(err, `Erro ao enviar a imagem "${file.name}".`)
      store.dispatch('setError', { message: result.message })
      return {
        ...result,
        completed: index,
        total: imageFiles.length,
        failedFile: file.name,
      }
    }
  }

  store.dispatch('setSuccess', {
    message: imageFiles.length === 1 ? 'Imagem enviada com sucesso!' : `${imageFiles.length} imagens enviadas com sucesso!`,
  })

  return {
    error: false,
    status: 204,
    completed: imageFiles.length,
    total: imageFiles.length,
  }
}

const getFile = async (params, fileName, headers, action, route = 'exporter/xlsx', content = null, includeDeleted = false) => {
  const result = await axiosInstance
    .post(
      route,
      { ...params, headers, action, content, includeDeleted },
      {
        responseType: 'blob',
      },
    )
    .then((res) => {
      const href = URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${fileName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
      return { ...res.data, status: res.status }
    })
    .catch((err) => {
      store.dispatch('setError', {
        message: `Erro ao baixar arquivo ${err.response?.message}!`,
      })
      return { error: err, status: err.response?.status }
    })

  return result
}

export { get, search, update, remove, batchRemove, insert, validateCurrentPassword, insertFile, insertImageFiles, validateImageFiles, getFile }
